import { TelemetryEvent, DataField, makeStringDataField, makeInt64DataField } from '@ms/oteljs';
import { Preprocessor } from './AriaSink';
import { sendEvent } from './AriaSendEvent';

export class EventThrottler implements Preprocessor {
  private _sinkName: string;
  private _maxSingleEventPerSecond?: number;
  private _maxTotalEventsPerSecond?: number;
  private _lastIntervalStart: number;
  private _totalEventsInLastInterval: number = 0;
  private _SingleEventInLastInterval: object = {};
  public _sendEvent: (event: TelemetryEvent, additionalDataFields: DataField[]) => void;

  private TELEMETRY_THROTTLED_EVENT: TelemetryEvent = {
    eventName: 'Office.Telemetry.OTelJS.EventThrottled',
    telemetryProperties: {
      ariaTenantToken: 'f998cc5ba4d448d6a1e8e913ff18be94-dd122e0a-fcf8-4dc5-9dbb-6afac5325183-7405',
      nexusTenantToken: 1723
    }
  };

  constructor(sinkName: string) {
    this._sinkName = sinkName;
    this._lastIntervalStart = Date.now();
    this._sendEvent = sendEvent;
  }

  public setSingleEventThrottle(eventsPerSecond: number) {
    this._maxSingleEventPerSecond = eventsPerSecond;
  }

  public setTotalEventThrottle(eventsPerSecond: number) {
    this._maxTotalEventsPerSecond = eventsPerSecond;
  }

  /**
   * Determine whether an event can be sent. Generally returns true; throws if an event is invalid.
   * @param event An event to process
   */
  public processEvent(event: TelemetryEvent): boolean {
    if (this._maxSingleEventPerSecond || this._maxTotalEventsPerSecond) {
      const elapsedInterval = Date.now() - this._lastIntervalStart;
      if (elapsedInterval > 1000 /* milliseconds */) {
        this._lastIntervalStart = Date.now();
        this._totalEventsInLastInterval = 0;
        this._SingleEventInLastInterval = {};
      }
      if (this._maxSingleEventPerSecond) {
        if (event.eventName in this._SingleEventInLastInterval) {
          this._SingleEventInLastInterval[event.eventName]++;
        } else {
          this._SingleEventInLastInterval[event.eventName] = 1;
        }
        this.applyThrottle(
          this._SingleEventInLastInterval[event.eventName],
          this._maxSingleEventPerSecond,
          'MaxSingleEventsPerSecond',
          event.eventName
        );
      }
      if (this._maxTotalEventsPerSecond) {
        this._totalEventsInLastInterval++;
        this.applyThrottle(this._totalEventsInLastInterval, this._maxTotalEventsPerSecond, 'MaxTotalEventsPerSecond', event.eventName);
      }
    }
    return true;
  }

  /**
   * Applies a throttle
   * Returns true if the event can be sent
   * Throws and sends telemetry otherwise
   * @param eventCount The number of events seen so far
   * @param throttleLimit The number of events at which the throttle should be applied
   * @param throttleName The name of the throttle
   * @param eventName The name of the event
   */
  private applyThrottle(eventCount: number, throttleLimit: number, throttleName: string, eventName: string): boolean {
    if (eventCount > throttleLimit) {
      if (eventCount === throttleLimit + 1) {
        this._sendEvent(this.TELEMETRY_THROTTLED_EVENT, [
          makeStringDataField('SinkName', this._sinkName),
          makeStringDataField('ThrottledEvent.Name', eventName),
          makeInt64DataField(throttleName, throttleLimit)
        ]);
      }
      throw `Exceeded ${throttleLimit} ${throttleName}`;
    }
    return true;
  }
}
